import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ShieldCheck, AlertTriangle, Plus, Pencil, KeyRound, Trash2, Power, Activity, Users, FileSpreadsheet, Loader2, Layers, Clock, UserX, EyeOff } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { CreateEditModal, ResetModal, DeleteModal, ActivityModal, RosterModal } from "./ManageUsersModals";

const INK = "#141412";
const SCOPES = [{ k: "today", label: "Today" }, { k: "5d", label: "5 days" }, { k: "mtd", label: "MTD" }, { k: "all", label: "All" }];

const VERIFY = {
  verified: { label: "ID Verified", c: "#10b981", Icon: ShieldCheck },
  in_progress: { label: "Verifying…", c: "#06b6d4", Icon: ShieldCheck },
  in_review: { label: "ID In Review", c: "#f59e0b", Icon: ShieldCheck },
  reverify_required: { label: "Re-verify", c: "#f59e0b", Icon: AlertTriangle },
  blocked: { label: "ID Blocked", c: "#ef4444", Icon: AlertTriangle },
};
const etDate = (ts) => { if (!ts) return "—"; try { return new Date(ts).toLocaleString("en-US", { timeZone: "America/New_York" }) + " ET"; } catch { return ts; } };
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const IDLE_DAYS = 15;
const isIdle = (r) => r.is_active && !!r.last_login && (Date.now() - new Date(r.last_login).getTime()) / 86400000 >= IDLE_DAYS;
const neverLoggedIn = (r) => !r.last_login;
const LAYER_FALLBACK = { A: "Physicians / independent billing providers", B: "Other medical / clinical staff", C: "Billing & administration" };
// Our UI wording overrides the upstream taxonomy label for display only.
const LAYER_UI = { A: "Physicians / Rendering Providers" };

function VerifyChip({ v, id }) {
  if (!v) return null;
  const exempt = v.exempt === true;
  const notReq = (v.verification_required === false || v.state === "not_required") && v.state !== "verified";
  const m = exempt
    ? { label: "Exempt", c: "#64748b", Icon: ShieldCheck }
    : notReq
      ? { label: "Not required", c: "#64748b", Icon: ShieldCheck }
      : VERIFY[v.state] || { label: "ID Pending", c: "#9B9B97", Icon: ShieldCheck };
  const when = v.verified_at ? ` on ${new Date(v.verified_at).toLocaleDateString()}` : "";
  const title = exempt
    ? "Identity verification: Not required (admin-exempt)"
    : `Identity verification: ${m.label}${v.state === "verified" ? ` by Didit${when}` : ""}`;
  return (
    <span data-testid={`ml-row-${id}-verify`} title={title}
      className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase inline-flex items-center gap-1"
      style={{ background: `${m.c}20`, color: m.c, border: `1px solid ${m.c}45` }}>
      <m.Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

const LayerBadge = ({ label }) => label ? (
  <span title={label} className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase inline-block max-w-[190px] overflow-hidden text-ellipsis whitespace-nowrap align-middle" style={{ background: "#F1F1F4", color: "#6C6C69", border: "1px solid #E2E2DF", lineHeight: "1.5" }}>{label}</span>
) : null;

function UserRow({ r, me, isPrimary, alert, spend, spendPending, scope, busy, layerLabel, onEdit, onReset, onToggle, onDelete, onActivity }) {
  const name = `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.email;
  const display = r.credential ? `${name}, ${r.credential}` : name;
  return (
    <div data-testid={me ? "ml-me-row" : `ml-row-${r.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3.5 border-t border-[#E9E9EC] first:border-t-0">
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[14px] font-bold text-[var(--ink)]">{display}</span>
          {me ? <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase" style={{ background: INK, color: "#fff" }}>You</span> : null}
          {r.is_primary ? <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase" style={{ background: "#EEF2FF", color: "#4A4AC2", border: "1px solid #DADAF5" }}>Primary</span> : null}
          <LayerBadge label={layerLabel} />
          {r.can_manage_logins && !r.is_primary ? <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase" style={{ background: "#ECFDF5", color: "#047857", border: "1px solid #CBF0E0" }}>Can add users</span> : null}
          <VerifyChip v={r.verification} id={r.id} />
          {alert ? <span data-testid={`ml-row-${r.id}-alert`} title={(alert.reasons || []).join(" · ")} className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase inline-flex items-center gap-1" style={{ background: "#fef2f2", color: "#ef4444", border: "1px solid #fca5a5" }}><AlertTriangle className="w-3 h-3" /> Review</span> : null}
        </div>
        <div className="text-[12px] text-[var(--gray2)] mt-0.5 flex items-center gap-2 flex-wrap">
          <span>{r.email}</span>
          {!r.is_active ? <span style={{ color: "#B3261E", fontWeight: 700 }}>· Disabled</span> : null}
          <span>· {Array.isArray(r.allowed_modules) ? r.allowed_modules.length : 0} modules</span>
          <span>· Last login {etDate(r.last_login)}</span>
        </div>
      </div>

      <span data-testid={me ? "ml-me-spend" : `ml-row-${r.id}-spend`} title={spendPending ? "Updating…" : `${spend?.calls || 0} AI calls`} className="text-[11px] font-semibold px-2 py-1 rounded-full tabular-nums" style={{ background: "#F7F7F6", color: "#6C6C69", border: "1px solid #EEEEEC" }}>
        {spendPending ? "…" : money(spend?.cost_usd)} · {SCOPES.find((s) => s.k === scope)?.label}
      </span>

      <div className="flex items-center gap-1">
        <IconBtn testid={`ml-row-${r.id}-activity`} title="Login activity" onClick={() => onActivity(r)}><Activity size={15} /></IconBtn>
        <IconBtn testid={`ml-row-${(me ? "me" : r.id)}-edit`} title="Edit" onClick={() => onEdit(r, me)}><Pencil size={15} /></IconBtn>
        {!me ? <>
          <IconBtn testid={`ml-row-${r.id}-reset`} title="Reset password" onClick={() => onReset(r)}><KeyRound size={15} /></IconBtn>
          <IconBtn testid={`ml-row-${r.id}-toggle`} title={r.is_active ? "Disable" : "Enable"} disabled={busy} onClick={() => onToggle(r)}><Power size={15} color={r.is_active ? undefined : "#B3261E"} /></IconBtn>
          <IconBtn testid={`ml-row-${r.id}-delete`} title="Delete" danger onClick={() => onDelete(r)}><Trash2 size={15} /></IconBtn>
        </> : null}
      </div>
    </div>
  );
}

const IconBtn = ({ children, onClick, title, testid, danger, disabled }) => (
  <button onClick={onClick} title={title} aria-label={title} data-testid={testid} disabled={disabled}
    className="w-8 h-8 rounded-lg grid place-items-center transition hover:bg-black/[.05] disabled:opacity-40 disabled:pointer-events-none"
    style={{ color: danger ? "#B3261E" : "#6C6C69" }}>{children}</button>
);

export default function ManageUsers({ profile }) {
  const [rows, setRows] = useState([]);
  const [me, setMe] = useState(profile || null);
  const [modules, setModules] = useState([]);
  const [taxonomy, setTaxonomy] = useState([]);
  const [alerts, setAlerts] = useState({});
  const [scope, setScope] = useState("mtd");
  const [spendData, setSpendData] = useState({ scope: null, map: {} });
  const spendReqRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // modal state
  const [editing, setEditing] = useState(null);   // { mode, row }
  const [resetting, setResetting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [activity, setActivity] = useState(null);
  const [rosterOpen, setRosterOpen] = useState(false);

  const isPrimary = !!me?.is_primary;
  const tenantCode = me?.tenant_code;
  const [groupBy, setGroupBy] = useState(false);
  const [filterMode, setFilterMode] = useState("all"); // all | idle | never
  const [hideDisabled, setHideDisabled] = useState(false);
  const layerClass = useCallback((layer) => {
    if (!layer) return "Unassigned";
    if (LAYER_UI[layer]) return LAYER_UI[layer];
    const t = (taxonomy || []).find((x) => x.layer === layer);
    if (t?.label && t.label.includes("—")) return t.label.split("—")[1].trim();
    return LAYER_FALLBACK[layer] || `Layer ${layer}`;
  }, [taxonomy]);

  const load = useCallback(async () => {
    setErr("");
    try {
      const [p, sl, md, tx, al, vs] = await Promise.all([
        api.get("/api/ehibridge/auth/profile"),
        api.get("/api/edi/sub-logins"),
        api.get("/api/edi/sub-logins/modules"),
        api.get("/api/edi/sub-logins/credential-taxonomy"),
        api.get("/api/edi/sub-logins/login-alerts").catch(() => ({ data: { alerts: {} } })),
        api.get("/api/edi/verification/status").catch(() => ({ data: null })),
      ]);
      const meRow = { ...p.data, verification: vs.data ? { state: vs.data.state, verified_at: vs.data.verified_at, exempt: vs.data.exempt, verification_required: vs.data.verification_required } : undefined };
      setMe(meRow);
      setRows(sl.data?.sub_logins || []);
      setModules(md.data?.modules || []);
      setTaxonomy(tx.data?.taxonomy || []);
      setAlerts(al.data?.alerts || {});
    } catch (e) { setErr(describeError(e)); } finally { setLoading(false); }
  }, []);

  const loadSpend = useCallback(async (sc) => {
    const reqId = ++spendReqRef.current;
    try {
      const { data } = await api.get(`/api/edi/ai-usage/login-spend?scope=${sc}`);
      if (reqId !== spendReqRef.current) return; // a newer scope was requested — ignore this stale response
      const map = {}; (data?.by_login || []).forEach((x) => { map[x.user_id] = x; });
      setSpendData({ scope: sc, map });
    } catch { if (reqId === spendReqRef.current) setSpendData({ scope: sc, map: {} }); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSpend(scope); }, [scope, loadSpend]);

  const list = useMemo(() => rows.filter((r) => r.id !== me?.id), [rows, me?.id]);
  const idleCount = useMemo(() => list.filter(isIdle).length, [list]);
  const neverCount = useMemo(() => list.filter(neverLoggedIn).length, [list]);
  const shown = useMemo(() => {
    let base = filterMode === "idle" ? list.filter(isIdle) : filterMode === "never" ? list.filter(neverLoggedIn) : list;
    if (hideDisabled) base = base.filter((r) => r.is_active);
    return base;
  }, [list, filterMode, hideDisabled]);

  // The pinned "You" row must respect the active filters too — otherwise it shows
  // even when it doesn't match (e.g. under Idle, despite having logged in today).
  const meMatches = useMemo(() => {
    if (!me) return false;
    if (hideDisabled && me.is_active === false) return false;
    if (filterMode === "idle") return isIdle(me);
    if (filterMode === "never") return neverLoggedIn(me);
    return true;
  }, [me, filterMode, hideDisabled]);

  const [togglingId, setTogglingId] = useState(null);
  const togglingRef = useRef(false);
  const onEdit = (r, isMe) => setEditing({ mode: isMe ? "self" : "edit", row: r });
  const onToggle = async (r) => {
    if (togglingRef.current) return;
    togglingRef.current = true;
    setTogglingId(r.id);
    try { await api.patch(`/api/edi/sub-logins/${r.id}`, { is_active: !r.is_active }); await load(); }
    catch (e) { setErr(describeError(e)); } finally { togglingRef.current = false; setTogglingId(null); }
  };
  const afterWrite = () => { setEditing(null); setResetting(null); setDeleting(null); load(); };

  if (loading) {
    return <div data-testid="ml-panel" className="flex items-center gap-2 text-[13px] text-[var(--gray2)] py-16 justify-center"><Loader2 size={16} className="animate-spin" /> Loading users…</div>;
  }

  return (
    <div data-testid="ml-panel" className="fade-up">
      {/* header */}
      <div className="flex items-start gap-3 flex-wrap mb-5">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2">
            <h2 className="text-[20px] font-extrabold text-[var(--ink)] tracking-[-.01em]">Manage Users</h2>
            {tenantCode ? <span data-testid="ml-tenant-badge" className="text-[11px] font-semibold px-2.5 py-[3px] rounded-full border border-[#E9E9EC] bg-white text-[var(--ink2)] tabular-nums">Aria Cluster ID: {tenantCode}</span> : null}
          </div>
          <p className="text-[13px] text-[var(--gray2)] mt-1">Add logins for your team, set their qualification and access, and review sign-in activity.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setRosterOpen(true)} data-testid="ml-roster-open" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 rounded-full border border-[#E9E9EC] text-[var(--ink2)] hover:border-[var(--gray3)] transition"><FileSpreadsheet size={14} /> Credential roster</button>
          <button onClick={() => setEditing({ mode: "create", row: null })} data-testid="ml-add-user" className="inline-flex items-center gap-1.5 text-[12.5px] font-bold px-3.5 py-2 rounded-full text-white transition hover:opacity-90" style={{ background: INK }}><Plus size={15} /> Add user</button>
        </div>
      </div>

      {err ? <div data-testid="ml-panel-error" className="text-[12.5px] mb-4" style={{ color: "#B3261E", background: "#FDEDEC", border: "1px solid #F3D3D0", borderRadius: 9, padding: "9px 11px" }}>{err}</div> : null}

      {/* controls: Lumina spend window + group-by + idle filter */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Users size={14} className="text-[var(--gray2)]" />
        <span className="text-[11px] text-[var(--gray2)] font-semibold mr-1">Ariaone Lumina Spend</span>
        <div className="inline-flex items-center gap-1 rounded-full border border-[#E9E9EC] p-1 bg-white">
          {SCOPES.map((s) => (
            <button key={s.k} onClick={() => setScope(s.k)} data-testid={`ml-spend-${s.k}`}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition ${scope === s.k ? "bg-[var(--ink)] text-white" : "text-[var(--ink2)] hover:bg-black/[.05]"}`}>{s.label}</button>
          ))}
        </div>
        <span className="w-px h-5 bg-[#E9E9EC] mx-1" />
        <button onClick={() => setGroupBy((v) => !v)} data-testid="ml-groupby-toggle" aria-pressed={groupBy}
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border transition ${groupBy ? "bg-[var(--ink)] text-white border-transparent" : "bg-white text-[var(--ink2)] border-[#E9E9EC] hover:border-[var(--gray3)]"}`}>
          <Layers size={13} /> Group by classification
        </button>
        <button onClick={() => setFilterMode((m) => (m === "idle" ? "all" : "idle"))} data-testid="ml-idle-toggle" aria-pressed={filterMode === "idle"}
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border transition ${filterMode === "idle" ? "bg-[#9A5A00] text-white border-transparent" : "bg-white text-[var(--ink2)] border-[#E9E9EC] hover:border-[var(--gray3)]"}`}>
          <Clock size={13} /> Idle 15+ days{idleCount ? <span className={`tabular-nums ${filterMode === "idle" ? "opacity-90" : "text-[var(--gray3)]"}`}>· {idleCount}</span> : null}
        </button>
        <button onClick={() => setFilterMode((m) => (m === "never" ? "all" : "never"))} data-testid="ml-never-toggle" aria-pressed={filterMode === "never"}
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border transition ${filterMode === "never" ? "bg-[#B3261E] text-white border-transparent" : "bg-white text-[var(--ink2)] border-[#E9E9EC] hover:border-[var(--gray3)]"}`}>
          <UserX size={13} /> Never signed in{neverCount ? <span className={`tabular-nums ${filterMode === "never" ? "opacity-90" : "text-[var(--gray3)]"}`}>· {neverCount}</span> : null}
        </button>
        <button onClick={() => setHideDisabled((v) => !v)} data-testid="ml-hidedisabled-toggle" aria-pressed={hideDisabled}
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border transition ${hideDisabled ? "bg-[var(--ink)] text-white border-transparent" : "bg-white text-[var(--ink2)] border-[#E9E9EC] hover:border-[var(--gray3)]"}`}>
          <EyeOff size={13} /> Hide disabled
        </button>
      </div>

      {/* Me row + list */}
      <div className="rounded-[16px] border border-[#E9E9EC] bg-white overflow-hidden">
        {me && meMatches ? <UserRow r={me} me isPrimary={isPrimary} spend={spendData.scope === scope ? spendData.map[me.id] : null} spendPending={spendData.scope !== scope} scope={scope} layerLabel={me.layer ? layerClass(me.layer) : null} onEdit={onEdit} onActivity={setActivity} /> : null}

        {groupBy ? (
          ["A", "B", "C", "_none"].map((L) => {
            const members = shown.filter((r) => (L === "_none" ? !r.layer : r.layer === L));
            if (members.length === 0) return null;
            return (
              <div key={L} data-testid={`ml-group-${L}`}>
                <div className="px-4 py-2 bg-[#FAFAFA] border-t border-[#E9E9EC] text-[11px] font-bold uppercase tracking-wide text-[var(--gray2)]">
                  {L === "_none" ? "Unassigned (no classification yet)" : layerClass(L)} <span className="text-[var(--gray3)]">· {members.length}</span>
                </div>
                {members.map((r) => (
                  <UserRow key={r.id} r={r} isPrimary={isPrimary} alert={alerts[r.id]} spend={spendData.scope === scope ? spendData.map[r.id] : null} spendPending={spendData.scope !== scope} scope={scope} busy={togglingId === r.id} layerLabel={r.layer ? layerClass(r.layer) : null}
                    onEdit={onEdit} onReset={setResetting} onToggle={onToggle} onDelete={setDeleting} onActivity={setActivity} />
                ))}
              </div>
            );
          })
        ) : (
          shown.map((r) => (
            <UserRow key={r.id} r={r} isPrimary={isPrimary} alert={alerts[r.id]} spend={spendData.scope === scope ? spendData.map[r.id] : null} spendPending={spendData.scope !== scope} scope={scope} busy={togglingId === r.id} layerLabel={r.layer ? layerClass(r.layer) : null}
              onEdit={onEdit} onReset={setResetting} onToggle={onToggle} onDelete={setDeleting} onActivity={setActivity} />
          ))
        )}

        {shown.length === 0 ? (
          <div data-testid="ml-empty" className="px-4 py-6 text-[13px] text-[var(--gray3)] border-t border-[#E9E9EC]">
            {filterMode === "idle" ? "No active users have been idle for 15+ days."
              : filterMode === "never" ? "Everyone has signed in at least once."
              : "No other users yet. Use “Add user” to create a login for a teammate."}
          </div>
        ) : null}
      </div>

      {/* modals */}
      {editing ? <CreateEditModal mode={editing.mode} row={editing.row} isPrimary={isPrimary} modules={modules} taxonomy={taxonomy} onClose={() => setEditing(null)} onSaved={afterWrite} /> : null}
      {resetting ? <ResetModal row={resetting} onClose={() => setResetting(null)} onSaved={() => setResetting(null)} /> : null}
      {deleting ? <DeleteModal row={deleting} onClose={() => setDeleting(null)} onDeleted={afterWrite} /> : null}
      {activity ? <ActivityModal row={activity} onClose={() => setActivity(null)} /> : null}
      {rosterOpen ? <RosterModal tenantCode={tenantCode} onClose={() => setRosterOpen(false)} /> : null}
    </div>
  );
}
